import { type ReactNode, useId } from 'react'
import { NumberIcon } from '../../../../components/ui-components/NumberIcon'
import RenderHTML from '../../../../components/ui-components/RenderHTML'
import { EliteScoreTrends } from '../../../../types/mutual-fund-detail.types'
import { EventNames } from '../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../utils/appBridge'
import { PlansLumpsumSheetEventIds, PlansScreen } from '../analytics/plansEvents'
import {
  PlanRecommendationReasonHighlightStrip,
  resolveEliteScoreTrendFromPerformanceTag
} from './PlanRecommendationReasonHighlightStrip'

export type PlanRecommendationReasonPointerTone = 'positive' | 'warning'

type PlanRecommendationReasonPointerProps = {
  index: number
  badgeLabel?: string | number
  showTopDivider?: boolean
  title: ReactNode
  subtitle: string
  subtitleClassName?: string
  highlightTitle?: string
  highlightDescription?: string
  highlightContent?: ReactNode
  tone?: PlanRecommendationReasonPointerTone
  analyticsScreen?: string
}

export function PlanRecommendationReasonPointer({
  index,
  badgeLabel,
  showTopDivider = false,
  title,
  subtitle,
  subtitleClassName = '',
  highlightTitle,
  highlightDescription,
  highlightContent,
  tone = 'positive',
  analyticsScreen = PlansScreen.LumpsumAllocationRecommendation
}: PlanRecommendationReasonPointerProps) {
  const shouldShowHighlight =
    highlightContent != null ||
    (highlightTitle?.length ?? 0) > 0 ||
    (highlightDescription?.length ?? 0) > 0

  return (
    <div className={`px-5 py-4 ${showTopDivider ? 'border-t border-[#F0F4F7]' : ''}`}>
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center">
          <PlanRecommendationReasonPointerBadge index={index} badgeLabel={badgeLabel} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <div className="pt-[5px]">
            <div className="text-[14px] font-[500] leading-[140%] text-[#232323]">{title}</div>
            {subtitle ? (
              <div className={`text-[14px] font-[400] leading-[140%] text-[#232323] pt-1 w-[283px] ${subtitleClassName}`}>
                {subtitle}
              </div>
            ) : null}
          </div>
          {shouldShowHighlight ? (
            highlightContent ?? (
              <PlanRecommendationReasonPointerHighlightStrip
                highlightTitle={highlightTitle ?? ''}
                highlightDescription={highlightDescription ?? ''}
                tone={tone}
              />
            )
          ) : null}
        </div>
      </div>
    </div>
  )
}

function resolveReasonPointerStripTag(tone: PlanRecommendationReasonPointerTone): EliteScoreTrends {
  if (tone === 'warning') {
    return EliteScoreTrends.AVERAGE
  }
  return EliteScoreTrends.GOOD
}

export function PlanRecommendationReasonPointerHighlightStrip({
  highlightTitle,
  highlightDescription,
  tone = 'positive',
  onInfoClick,
  analyticsScreen = PlansScreen.LumpsumAllocationRecommendation,
  fundName = '',
  fundIsin = ''
}: {
  highlightTitle: string
  highlightDescription: string
  tone?: PlanRecommendationReasonPointerTone
  onInfoClick?: () => void
  analyticsScreen?: string
  fundName?: string
  fundIsin?: string
}) {
  const tag = resolveReasonPointerStripTag(tone)

  return (
    <PlanRecommendationReasonHighlightStrip tag={tag}>
      <div className="flex flex-col gap-[2px]">
        <div className="flex items-center gap-1">
          <div className="text-[18px] font-[500] leading-[150%]">{highlightTitle}</div>
          {onInfoClick ? (
            <div
              className="shrink-0 cursor-pointer"
              onClick={() => {
                lightImpactVibrate()
                trackEvent({
                  eventName: EventNames.UserClicked,
                  payload: {
                    ScreenName: analyticsScreen,
                    eventId: PlansLumpsumSheetEventIds.ReasonInfo,
                    buttonLabel: 'Reason info',
                    ...(fundName ? { fundName } : {}),
                    ...(fundIsin ? { fundIsin } : {})
                  }
                })
                onInfoClick()
              }}
            >
              <PlanRecommendationReasonPointerInfoIcon />
            </div>
          ) : null}
        </div>
        <div className="whitespace-pre-line text-[13px] font-[400] leading-[140%]">{highlightDescription}</div>
      </div>
    </PlanRecommendationReasonHighlightStrip>
  )
}

export function resolvePlanRecommendationReasonPointerBadgeLabel({
  totalPointerCount,
  showsPercentageHighlight
}: {
  totalPointerCount: number
  showsPercentageHighlight: boolean
}): '%' | undefined {
  if (totalPointerCount === 1 && showsPercentageHighlight) {
    return '%'
  }

  return undefined
}

export function PlanRecommendationReasonPointerBadge({
  index,
  badgeLabel
}: {
  index: number
  badgeLabel?: string | number
}) {
  if (badgeLabel === '%') {
    return <PlanRecommendationReasonPointerPercentageIcon />
  }
  if (typeof badgeLabel === 'number' || (typeof badgeLabel === 'string' && badgeLabel.length > 0)) {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#F0F4F7] text-[14px] font-[500] text-[#3D3D3D]">
        {badgeLabel}
      </div>
    )
  }
  return <NumberIcon number={index} />
}

export { resolveEliteScoreTrendFromPerformanceTag }

export function PlanRecommendationReasonPointerInfoIcon() {
  const clipPathId = useId()

  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <g clipPath={`url(#${clipPathId})`}>
        <path
          d="M8 0.5C10.5717 0.5 12.4232 1.1537 13.6348 2.36523C14.8463 3.57678 15.5 5.42834 15.5 8C15.5 10.5717 14.8463 12.4232 13.6348 13.6348C12.4232 14.8463 10.5717 15.5 8 15.5C5.42834 15.5 3.57678 14.8463 2.36523 13.6348C1.1537 12.4232 0.5 10.5717 0.5 8C0.5 5.42834 1.15369 3.57678 2.36523 2.36523C3.57678 1.15369 5.42834 0.5 8 0.5Z"
          fill="white"
          stroke="#FFE2BF"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6.37667 10.2069C6.00306 10.2069 5.7002 10.5023 5.7002 10.8667C5.7002 11.2312 6.00306 11.5266 6.37667 11.5266H9.62372C9.99733 11.5266 10.3002 11.2312 10.3002 10.8667C10.3002 10.5023 9.99733 10.2069 9.62372 10.2069H8.67667V7.17164C8.67667 6.80722 8.3738 6.51181 8.0002 6.51181H6.91784C6.54424 6.51181 6.24137 6.80722 6.24137 7.17164C6.24137 7.53606 6.54424 7.83148 6.91784 7.83148H7.32372V10.2069H6.37667ZM9.08255 4.5323C9.08255 5.11536 8.59796 5.58804 8.0002 5.58804C7.40243 5.58804 6.91784 5.11536 6.91784 4.5323C6.91784 3.94924 7.40243 3.47656 8.0002 3.47656C8.59796 3.47656 9.08255 3.94924 9.08255 4.5323Z"
          fill="#CC5C2D"
        />
      </g>
      <defs>
        <clipPath id={clipPathId}>
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  )
}

export function PlanRecommendationReasonPointerPercentageIcon() {
  return (
    <RenderHTML
      innerHTMLText={`<svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M0.75 16.75C0.75 20.9935 2.43571 25.0631 5.43629 28.0637C8.43687 31.0643 12.5065 32.75 16.75 32.75C20.9935 32.75 25.0631 31.0643 28.0637 28.0637C31.0643 25.0631 32.75 20.9935 32.75 16.75C32.75 12.5065 31.0643 8.43687 28.0637 5.43629C25.0631 2.43571 20.9935 0.75 16.75 0.75C12.5065 0.75 8.43687 2.43571 5.43629 5.43629C2.43571 8.43687 0.75 12.5065 0.75 16.75Z" stroke="#F0F4F7" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M13.4858 15.8192C14.7724 15.8192 15.8153 14.7763 15.8153 13.4897C15.8153 12.2031 14.7724 11.1602 13.4858 11.1602C12.1992 11.1602 11.1562 12.2031 11.1562 13.4897C11.1562 14.7763 12.1992 15.8192 13.4858 15.8192Z" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M20.0092 22.3427C21.2958 22.3427 22.3388 21.2997 22.3388 20.0131C22.3388 18.7266 21.2958 17.6836 20.0092 17.6836C18.7227 17.6836 17.6797 18.7266 17.6797 20.0131C17.6797 21.2997 18.7227 22.3427 20.0092 22.3427Z" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M11.1562 22.342L22.3381 11.1602" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`}
    />
  )
}
